import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, Trash2, X, Check, XCircle, ShieldCheck, KeyRound, Search } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  admin: "bg-indigo-100 text-indigo-700",
  ventas: "bg-blue-100 text-red-700",
  operaciones: "bg-amber-100 text-amber-700",
  sucursal: "bg-fuchsia-100 text-fuchsia-700",
  cliente: "bg-emerald-100 text-emerald-700",
  almacen: "bg-orange-100 text-orange-700",
  contable: "bg-teal-100 text-teal-700",
  conductor: "bg-lime-100 text-lime-700",
};

const EMPTY = { fullname: "", email: "", username: "", password: "", role: "cliente", oficina: "", provincias: "", pais: "", main: false, warehouse_id: "" };

export default function Users() {
  const { api, user: current } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [access, setAccess] = useState(null);   // { id, fullname, username, email, password }
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((u) =>
      [u.fullname, u.username, u.email, u.oficina, u.role, u.numero_cuenta, u.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const load = () => {
    api.get("/users").then((r) => setRows(r.data));
    api.get("/users/pending").then((r) => setPending(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.role === "sucursal" && typeof payload.provincias === "string") {
        payload.provincias = payload.provincias.split(",").map((s) => s.trim()).filter(Boolean);
      } else {
        delete payload.provincias;
      }
      if (payload.role !== "almacen") delete payload.warehouse_id;
      await api.post("/users", payload);
      toast.success(t("user_created"));
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("user_exists"));
    }
  };

  const saveAccess = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { fullname: access.fullname, username: access.username, email: access.email };
      if (access.password) payload.password = access.password;
      await api.patch(`/users/${access.id}`, payload);
      toast.success(t("access_updated"));
      if (access.id === current.id && access.password) toast.warning(t("relogin_needed"));
      setAccess(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const approve = async (id) => {
    await api.post(`/users/${id}/approve`);
    toast.success(t("saved"));
    load();
  };
  const reject = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.post(`/users/${id}/reject`);
    toast.success(t("saved"));
    load();
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      {pending.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="pending-block">
          <h3 className="font-display font-semibold text-base flex items-center gap-2 text-amber-900 mb-3">
            <ShieldCheck className="w-5 h-5 text-amber-600" /> {t("pending_approvals")} ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="bg-white border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-slate-900">{u.fullname}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.auth_provider || "password"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(u.id)} data-testid={`approve-${u.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1">
                    <Check className="w-3 h-3" /> {t("approve")}
                  </button>
                  <button onClick={() => reject(u.id)} data-testid={`reject-${u.id}`} className="bg-slate-600 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> {t("reject")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold">{t("users")} <span className="text-xs font-normal text-slate-500">· {filtered.length} / {rows.length}</span></h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="users-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_users") || "Buscar usuario · código de cuenta · ID"}
              className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 w-72"
            />
          </div>
          <button data-testid="new-user-btn" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> {t("new")}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("name")}</th>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("username")}</th>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("email")}</th>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("account_code") || "Código"}</th>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("role")}</th>
              <th className="p-3 text-start font-semibold uppercase text-[11px] tracking-wider">{t("office")}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">—</td></tr>}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                <td className="p-3 font-medium text-slate-900">{u.fullname}</td>
                <td className="p-3 text-slate-600">{u.username}</td>
                <td className="p-3 text-slate-600">{u.email}</td>
                <td className="p-3 font-mono text-[11px] text-indigo-600">
                  {u.numero_cuenta || <span className="text-slate-300">—</span>}
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${ROLE_COLORS[u.role]}`}>{t("role_" + u.role) || t(u.role) || u.role}</span>
                  {u.role === "sucursal" && u.main && <span className="ms-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-amber-500 text-white" data-testid={`main-badge-${u.username}`}>{t("main") || "Principal"}</span>}
                </td>
                <td className="p-3 text-slate-600 text-xs">
                  {u.oficina || (u.warehouse_id ? warehouses.find(w => w.id === u.warehouse_id)?.nombre || "warehouse" : "—")}
                  {u.pais && <span className="block text-[10px] text-slate-400">{u.pais}</span>}
                </td>
                <td className="p-3 text-end">
                  <div className="flex items-center gap-1 justify-end">
                    <button data-testid={`edit-access-${u.username}`} title={t("edit_access")}
                      onClick={() => setAccess({ id: u.id, fullname: u.fullname || "", username: u.username || "", email: u.email || "", password: "" })}
                      className="text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg"><KeyRound className="w-4 h-4" /></button>
                    {u.id !== current.id && (
                      <button data-testid={`del-user-${u.username}`} onClick={() => remove(u.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {access && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveAccess} className="bg-white rounded-xl w-full max-w-md p-6 space-y-4" data-testid="access-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-indigo-600" /> {t("edit_access")}</h3>
              <button type="button" onClick={() => setAccess(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {[
              { k: "fullname", label: t("fullname") },
              { k: "username", label: t("username") },
              { k: "email", label: t("email"), type: "email" },
            ].map(f => (
              <div key={f.k}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</label>
                <input required type={f.type || "text"} data-testid={`ac-${f.k}`} value={access[f.k]}
                  onChange={(e) => setAccess({ ...access, [f.k]: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("new_password")}</label>
              <input type="text" data-testid="ac-password" value={access.password} autoComplete="new-password"
                onChange={(e) => setAccess({ ...access, password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:border-indigo-600" />
              <p className="text-[10px] text-slate-500 mt-1">{t("leave_blank_keep")}</p>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setAccess(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="ac-submit" className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{t("new_user")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {[
              { k: "fullname", label: t("fullname") },
              { k: "email", label: t("email"), type: "email" },
              { k: "username", label: t("username") },
              { k: "password", label: t("password") },
            ].map((f) => (
              <div key={f.k}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</label>
                <input required type={f.type || "text"} data-testid={`uf-${f.k}`} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("role")}</label>
              <select data-testid="uf-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option value="cliente">{t("cliente")}</option>
                <option value="ventas">{t("ventas")}</option>
                <option value="operaciones">{t("operaciones")}</option>
                <option value="sucursal">{t("sucursal")}</option>
                <option value="almacen">{t("role_almacen")}</option>
                <option value="contable">{t("role_contable")}</option>
                <option value="conductor">{t("role_conductor")}</option>
                <option value="admin">{t("admin")}</option>
              </select>
            </div>
            {form.role === "sucursal" && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("office")}</label>
                  <input required data-testid="uf-oficina" value={form.oficina} onChange={(e) => setForm({ ...form, oficina: e.target.value })} placeholder="Sucursal Monterrey" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">{t("country") || "País"}</label>
                    <input data-testid="uf-pais" value={form.pais} onChange={(e) => setForm({ ...form, pais: e.target.value })} placeholder="Siria" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">&nbsp;</label>
                    <label className="flex items-center gap-2 px-3 py-2 border-2 border-amber-200 bg-amber-50 rounded-lg text-xs font-semibold text-amber-900 cursor-pointer">
                      <input type="checkbox" data-testid="uf-main" checked={!!form.main} onChange={(e) => setForm({ ...form, main: e.target.checked })} />
                      {t("main_office") || "Oficina principal del país"}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("provincias_covered")}</label>
                  <input data-testid="uf-provincias" value={form.provincias} onChange={(e) => setForm({ ...form, provincias: e.target.value })} placeholder="Damascus, Aleppo, Homs" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  <p className="text-[10px] text-slate-500 mt-1">{t("provincias_hint")}</p>
                </div>
              </>
            )}
            {form.role === "almacen" && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("warehouse")} *</label>
                <select required data-testid="uf-warehouse" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">—</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.nombre}</option>)}
                </select>
                {warehouses.length === 0 && <p className="text-[10px] text-amber-600 mt-1">{t("no_warehouses")}. Crea uno en Almacenes primero.</p>}
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="uf-submit" className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
